import { useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";

interface LeaseDoc {
  _id: string;
  filename: string;
  uploadedAt: number;
  url: string | null;
}

// Landlord-side doc manager — list + upload + delete for a single
// lease. Used inline inside each LeaseCard on /tenants.
export function LeaseDocsManager({ leaseId }: { leaseId: string }) {
  const docs = useQuery(anyApi.leases.docsForLease, { leaseId }) as
    | LeaseDoc[]
    | undefined;
  const generateUploadUrl = useMutation(anyApi.leases.generateUploadUrl);
  const attachDoc = useMutation(anyApi.leases.attachDoc);
  const removeDoc = useMutation(anyApi.leases.removeDoc);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const uploadUrl = (await generateUploadUrl({ leaseId })) as string;
        const res = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!res.ok) {
          throw new Error(`upload failed: ${res.status} ${res.statusText}`);
        }
        const { storageId } = (await res.json()) as { storageId: string };
        await attachDoc({ leaseId, storageId, filename: file.name });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemove = async (docId: string, filename: string) => {
    if (!confirm(`Delete "${filename}"? This can't be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await removeDoc({ docId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
          Documents
        </h4>
        <label className="cursor-pointer">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="sr-only"
            disabled={busy}
          />
          <span className="inline-block rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            {busy ? "Uploading…" : "Upload"}
          </span>
        </label>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      {docs === undefined ? (
        <p className="mt-2 text-xs text-slate-500">Loading…</p>
      ) : docs.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">No documents yet.</p>
      ) : (
        <ul className="mt-2 divide-y divide-slate-100">
          {docs.map((d) => (
            <li
              key={d._id}
              className="py-2 flex items-center justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                {d.url ? (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-slate-900 hover:underline truncate block"
                  >
                    {d.filename}
                  </a>
                ) : (
                  <span className="text-sm text-slate-500 truncate block">
                    {d.filename}
                  </span>
                )}
                <p className="text-[11px] text-slate-500">
                  Uploaded {formatTimestamp(d.uploadedAt)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(d._id, d.filename)}
                disabled={busy}
                className="shrink-0 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
